import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  LinearProgress,
  Alert,
  Divider,
  Grid,
  IconButton,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Close, FilterList, Cancel } from "@mui/icons-material";
import { debounce } from "lodash";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import DataTable from "../../widgets/DataTable";
import { roundToMaxDecimals } from "../../utils/util";
import { useNavigate } from "react-router-dom";

const SubscriptionAccounts = () => {
  // State for filters and pagination
  const navigate = useNavigate();
  const [planFilter, setPlanFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [customerIdFilter, setCustomerIdFilter] = useState("");
  const [customerIdInput, setCustomerIdInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Available filter options
  const planOptions = ["Plan1", "Plan2", "Plan3", "Plan4", "null"];
  const statusOptions = ["active", "expired", "expiringSoon"];

  // Build query string for API
  const queryString = new URLSearchParams({
    ...(planFilter.length > 0 && { plan: planFilter.join(",") }),
    ...(statusFilter.length > 0 && { status: statusFilter.join(",") }),
    ...(customerIdFilter && { customerId: customerIdFilter }),
    page,
    limit,
  }).toString();

  // Fetch data using useApiEp
  const { data, isLoading, isError, error } = useApiEp(
    `/root/accounts-list?${queryString}`
  );
  const { data: statsD } = useApiEp("/root/subscription-stats");

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [planFilter, statusFilter, customerIdFilter]);

  // Debounced function to update customerIdFilter
  const debouncedSetCustomerIdFilter = debounce((value) => {
    setCustomerIdFilter(value);
  }, 500);

  // Handle filter changes
  const handlePlanChange = (event) => {
    const value = event.target.value;
    setPlanFilter(typeof value === "string" ? value.split(",") : value);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setStatusFilter(typeof value === "string" ? value.split(",") : value);
  };

  const handleCustomerIdChange = (event) => {
    const value = event.target.value;
    setCustomerIdInput(value); // Update input immediately for smooth typing
    debouncedSetCustomerIdFilter(value); // Debounce filter update for API
  };

  // Handle removing individual filter chips
  const handleDeletePlanFilter = (filterToDelete) => () => {
    setPlanFilter((filters) =>
      filters.filter((filter) => filter !== filterToDelete)
    );
  };

  const handleDeleteStatusFilter = (filterToDelete) => () => {
    setStatusFilter((filters) =>
      filters.filter((filter) => filter !== filterToDelete)
    );
  };

  const handleDeleteCustomerIdFilter = () => {
    setCustomerIdInput("");
    setCustomerIdFilter("");
  };

  // Clear all filters
  const clearAllFilters = () => {
    setPlanFilter([]);
    setStatusFilter([]);
    setCustomerIdInput("");
    setCustomerIdFilter("");
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Get status label and color
  const getStatusInfo = (expiryDate) => {
    const currentDate = new Date();
    const sevenDaysFromNow = new Date(
      currentDate.getTime() + 7 * 24 * 60 * 60 * 1000
    );
    const expiry = new Date(expiryDate);

    if (expiry < currentDate) {
      return { label: "Expired", color: "error" };
    } else if (expiry <= sevenDaysFromNow) {
      return { label: "Expiring Soon", color: "warning" };
    } else {
      return { label: "Active", color: "success" };
    }
  };

  // Get plan color for Chip
  const getPlanColor = (planName) => {
    const planColors = {
      Plan2: "primary",
      Plan3: "secondary",
      Plan1: "info",
      Plan4: "success",
      null: "default",
    };
    return planColors[planName] || "default";
  };

  const handleAction = (action, id) => {
    switch (action) {
      case "view":
        navigate(`/home/${id}/account-usage`);
        break;
      case "edit":
        //navigate(`/home/accounts/${id}/edit-account`);
        break;
      case "delete":
        break;
      default:
        console.warn("unknown action", id);
    }
  };

  return (
    <Container sx={{ mt: 3, mb: 4 }}>
      <BackBtn />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          fontWeight="bold"
          color="primary"
        >
          Accounts List
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Filter and browse accounts by subscription plan, status, or customer
          ID
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }} variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterList color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Filters
            </Typography>
            {(planFilter.length > 0 ||
              statusFilter.length > 0 ||
              customerIdFilter) && (
              <Tooltip title="Clear all filters">
                <IconButton
                  size="small"
                  onClick={clearAllFilters}
                  sx={{ ml: 1 }}
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Divider sx={{ mb: 3 }} />

          {/* Active Filters Display */}
          {(planFilter.length > 0 ||
            statusFilter.length > 0 ||
            customerIdFilter) && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                color="textSecondary"
              >
                Active Filters:
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {planFilter.map((plan) => (
                  <Chip
                    key={plan}
                    label={`Plan: ${plan === "null" ? "No Plan" : plan}`}
                    color={getPlanColor(plan)}
                    onDelete={handleDeletePlanFilter(plan)}
                    deleteIcon={<Close />}
                    variant="outlined"
                  />
                ))}
                {statusFilter.map((status) => (
                  <Chip
                    key={status}
                    label={`Status: ${
                      status.charAt(0).toUpperCase() + status.slice(1)
                    }`}
                    color={
                      status === "active"
                        ? "success"
                        : status === "expired"
                        ? "error"
                        : "warning"
                    }
                    onDelete={handleDeleteStatusFilter(status)}
                    deleteIcon={<Close />}
                    variant="outlined"
                  />
                ))}
                {customerIdFilter && (
                  <Chip
                    label={`Customer ID: ${customerIdFilter}`}
                    color="info"
                    onDelete={handleDeleteCustomerIdFilter}
                    deleteIcon={<Close />}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  multiple
                  value={planFilter}
                  onChange={handlePlanChange}
                  label="Subscription Plan"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value === "null" ? "No Plan" : value}
                          color={getPlanColor(value)}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {planOptions.map((plan) => (
                    <MenuItem key={plan} value={plan}>
                      {plan === "null" ? "No Plan" : plan}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  onChange={handleStatusChange}
                  label="Status"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value.charAt(0).toUpperCase() + value.slice(1)}
                          color={
                            value === "active"
                              ? "success"
                              : value === "expired"
                              ? "error"
                              : "warning"
                          }
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Customer ID"
                value={customerIdInput}
                onChange={handleCustomerIdChange}
                variant="outlined"
                InputProps={{
                  endAdornment: customerIdInput && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleDeleteCustomerIdFilter}
                        edge="end"
                      >
                        <Close />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error State */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading accounts: {error?.message}
        </Alert>
      )}

      {/* Accounts Table */}
      {!isLoading && !isError && data && (
        <Card sx={{ borderRadius: 2 }} variant="outlined">
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Accounts ({data.totalItems})
              </Typography>

              {/* Summary Stats */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Chip
                  label={`Active: ${statsD?.summary?.totalActive || 0}`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Expiring: ${statsD?.summary?.totalExpiringSoon || 0}`}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Expired: ${statsD?.summary?.totalExpired || 0}`}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <DataTable
              sx={{ mb: 1 }}
              columns={[
                {
                  id: "serial",
                  label: "S.No.",
                  render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
                },
                { id: "customerId", label: "Name" },
                { id: "phoneNumberId", label: "Ph Number Id" },
                {
                  id: "subscriptionPlan",
                  label: "Plan",
                  render: (v) =>
                    v ? (
                      <Chip
                        label={v || "No Plan"}
                        color={getPlanColor(v)}
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      "NA"
                    ),
                },
                {
                  id: "expiryDate",
                  label: "Expiry Date",
                  render: (v) =>
                    v
                      ? new Date(v).toLocaleString("en-IN", {
                          dateStyle: "medium",
                        })
                      : "NA",
                },
                {
                  id: "wallet",
                  label: "Wallet",
                  render: (v) =>
                    v === 0 || v ? (
                      <Chip
                        label={`${roundToMaxDecimals(v, 2)}`}
                        variant="outlined"
                        size="small"
                        color="info"
                      />
                    ) : (
                      "NA"
                    ),
                },
                {
                  id: "status",
                  label: "Status",
                  render: (_v, row) => {
                    const { label: statusLabel, color: statusColor } =
                      getStatusInfo(row.expiryDate);
                    return (
                      <Chip
                        label={statusLabel}
                        color={statusColor}
                        size="small"
                      />
                    );
                  },
                },
              ]}
              actions={["view"]}
              rows={data?.data ?? []}
              page={page}
              itemsPerPage={limit}
              totalPages={data?.totalPages ?? 1}
              onPageChange={setPage}
              onAction={handleAction}
              itemsPerPageOptions={[20, 30, 50, 100]}
              onItemsPerPageChange={(lm) => {
                setLimit(lm);
              }}
              paginationAlign="end"
            />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default SubscriptionAccounts;
